import type { Auth0ProviderOptions } from '@auth0/auth0-react'

/**
 * Must stay origin-based: do NOT fold `import.meta.env.BASE_URL` in here.
 *
 * Auth0 validates `redirect_uri` against an explicit allowlist (managed in terraform,
 * `auth0/family-web`) and that allowlist cannot contain path wildcards, so only the root
 * `/login/callback` is ever accepted. A base-path-aware value such as
 * `/PR-123/login/callback` fails with "Callback URL mismatch" on Auth0's own domain,
 * before this app loads, so it cannot be caught or handled here.
 *
 * Preview builds therefore sign in through the root callback. Carrying the user from there
 * back into `/PR-<n>/` is a hard navigation, added with the preview workflow itself; this
 * PR only guarantees the callback URL stays authorizable. The invariant is pinned by
 * `__tests__/auth0.test.ts`.
 */
const getRedirectUri = (): string => {
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/login/callback`
  }
  return '/login/callback'
}

const auth0Config: Auth0ProviderOptions = {
  domain: import.meta.env['VITE_AUTH0_DOMAIN'],
  clientId: import.meta.env['VITE_AUTH0_CLIENT_ID'],
  authorizationParams: {
    redirect_uri: getRedirectUri(),
    response_type: 'code',
    scope: 'openid profile email offline_access',
    audience: import.meta.env['VITE_AUTH0_AUDIENCE'],
  },
  useRefreshTokens: true,
  cacheLocation: 'localstorage',
}

export default auth0Config
